import React from 'react';
import styled from 'styled-components';
import Icon, { HomeOutlined, LockOutlined, KeyOutlined } from '@ant-design/icons';
import { Button, Space, Card, Divider, Typography, Image, Descriptions, List } from 'antd';

import { useWizard } from 'components/shared/Wizard';
import { BedIcon, DogIcon, NoSmokingIcon } from 'ui-library/components/Icons';

import { getTranslatedString } from 'helpers/i18n';
import { toSentenceCase } from 'helpers/StringHelper';
import { formatMoney, formatDateTime } from 'helpers/Formatter';

const { Title } = Typography;

const StyledListItemMeta = styled(List.Item.Meta)`
  .ant-list-item-meta-title {
    font-family: unset;
    margin-top: 0;
    margin-bottom: 0;
  }
`;

const DATE_OPTIONS = Object.freeze({
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: null,
  minute: null,
});

function ApplicationOverview({
  applicationTemplateOverviewTitle,
  applicationTemplateOverviewText,
  application,
  mode,
  onClickCancel,
}) {
  const { previousStep, nextStep } = useWizard();

  const suiteDetails = [
    {
      title: getTranslatedString(
        `tenantPortal.applicationTemplate.baseApplication.suiteDetails.monthlyRent`
      ),
      icon: <HomeOutlined />,
      description: formatMoney(application?.lease?.lease_pricing?.rent_amount),
    },
    {
      title: getTranslatedString(
        `tenantPortal.applicationTemplate.baseApplication.suiteDetails.securityDeposit`
      ),
      icon: <LockOutlined />,
      description: `${formatMoney(
        application?.lease?.lease_pricing?.deposit_amount
      )} ${getTranslatedString(
        `tenantPortal.applicationTemplate.baseApplication.suiteDetails.dueAtMoveIn`
      )}`,
    },
    {
      title: getTranslatedString(
        `tenantPortal.applicationTemplate.baseApplication.suiteDetails.keyDeposit`
      ),
      icon: <KeyOutlined />,
      description: `${formatMoney(
        application?.lease?.lease_pricing?.key_deposit_amount
      )} ${getTranslatedString(
        `tenantPortal.applicationTemplate.baseApplication.suiteDetails.dueAtMoveIn`
      )}`,
    },
    {
      title: 'Unit type',
      icon: <Icon component={BedIcon} />,
      description: application?.unit?.unit_type_name || '0',
    },
    {
      title: getTranslatedString(
        'tenantPortal.applicationTemplate.baseApplication.suiteDetails.smoking'
      ),
      icon: <Icon component={NoSmokingIcon} />,
      description: application?.unit?.smoking
        ? getTranslatedString(
            'tenantPortal.applicationTemplate.baseApplication.suiteDetails.smokingAllowed'
          )
        : getTranslatedString(
            'tenantPortal.applicationTemplate.baseApplication.suiteDetails.noSmoking'
          ),
    },
    {
      title: getTranslatedString(
        'tenantPortal.applicationTemplate.baseApplication.suiteDetails.pets'
      ),
      icon: <Icon component={DogIcon} />,
      description: application?.unit?.pets_allowed
        ? getTranslatedString(
            'tenantPortal.applicationTemplate.baseApplication.suiteDetails.petFriendly'
          )
        : getTranslatedString(
            'tenantPortal.applicationTemplate.baseApplication.suiteDetails.noPets'
          ),
    },
  ];

  const leaseInformation = [
    {
      label: getTranslatedString('tenantPortal.lease.startDate'),
      value: formatDateTime(new Date(application.lease.lease_start_date), DATE_OPTIONS),
    },
    {
      label: getTranslatedString('showingDrawer.moveInDate'),
      value: formatDateTime(new Date(application.lease.early_move_in_date), DATE_OPTIONS),
    },
    {
      label: getTranslatedString('leaseTerm'),
      value: `${application.lease.lease_term_in_months} ${getTranslatedString('monthsPlural')}`,
    },
  ];

  const rentalIncentives = application.lease.rental_incentives.map((rentalIncentive) => ({
    label: rentalIncentive.name,
    value: rentalIncentive.description,
  }));

  const utilities = Object.entries(application.property.utilities || {}).map(
    ([utility, { paid_by }]) => ({
      label: toSentenceCase(utility),
      value: `${getTranslatedString('tenantPortal.applicationTemplate.paidForBy')} ${paid_by}`,
    })
  );

  const services = Object.entries(application.property.services || {}).map(
    ([service, { is_included }]) => ({
      label: toSentenceCase(service),
      value: is_included
        ? getTranslatedString('tenantPortal.applicationTemplate.baseApplication.services.included')
        : getTranslatedString(
            'tenantPortal.applicationTemplate.baseApplication.services.notIncluded'
          ),
    })
  );

  const mockDataSuffix =
    mode === 'preview'
      ? ` (${getTranslatedString('landlordPortal.applicationTemplate.preview.mockData')})`
      : null;

  function renderLeaseSection({ titleLocaleKey, data }) {
    if (!data.length) return null;

    return (
      <>
        <Title level={4} style={{ fontFamily: 'unset' }}>
          {getTranslatedString(
            `tenantPortal.applicationTemplate.baseApplication.${titleLocaleKey}`
          )}
          {mockDataSuffix}
        </Title>
        <Descriptions size="middle" bordered column={1}>
          {data.map((dataItem) => (
            <Descriptions.Item
              key={dataItem.label}
              contentStyle={{ width: '50%' }}
              label={dataItem.label}
            >
              {dataItem.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
        <Divider />
      </>
    );
  }

  return (
    <Card>
      <Title level={4} style={{ fontFamily: 'unset' }}>
        {applicationTemplateOverviewTitle}
      </Title>
      <div dangerouslySetInnerHTML={{ __html: applicationTemplateOverviewText }}></div>
      <Divider />

      <>
        <Title level={4} style={{ fontFamily: 'unset' }}>
          {getTranslatedString('tenantPortal.applicationTemplate.baseApplication.title')}
          {mockDataSuffix}
        </Title>
        {application.property.hero_image_url && (
          <Image src={application.property.hero_image_url} width="100%"></Image>
        )}
        {application?.unit?.unit_number && (
          <Title level={5} style={{ fontFamily: 'unset' }}>
            Suite {application?.lease?.unit?.unit_number}
          </Title>
        )}
        <List
          grid={{
            gutter: 16,
            xs: 1,
            sm: 2,
            md: 3,
            lg: 3,
            xl: 3,
            xxl: 3,
          }}
          dataSource={suiteDetails}
          renderItem={(item) => (
            <List.Item>
              <StyledListItemMeta
                avatar={item.icon}
                title={item.title}
                description={item.description}
              />
            </List.Item>
          )}
        />
      </>
      <Divider />

      {renderLeaseSection({ titleLocaleKey: 'leaseInformation', data: leaseInformation })}
      {renderLeaseSection({ titleLocaleKey: 'incentives', data: rentalIncentives })}
      {renderLeaseSection({ titleLocaleKey: 'utilities', data: utilities })}
      {renderLeaseSection({ titleLocaleKey: 'services', data: services })}

      <Space>
        <Button type="primary" onClick={nextStep}>
          {getTranslatedString('next')}
        </Button>
        <Button type="default" onClick={previousStep}>
          {getTranslatedString('back')}
        </Button>
        <Button type="default" onClick={onClickCancel}>
          {getTranslatedString('cancel')}
        </Button>
      </Space>
    </Card>
  );
}

export default ApplicationOverview;
